/**
 * Clerk-access API — the barrister's granting controls.
 * See docs/04-ENGINEERING/CAPABILITIES/access-control.md.
 */
import { api } from './api';

export type CapabilityState = 'ALLOW' | 'RESTRICTED' | 'FORBIDDEN';

export interface ClerkOverride {
  matterId: string;
  caseName: string | null;
  capability: string;
  effect: 'ALLOW' | 'DENY';
}

export interface ClerkAccess {
  capabilities: Record<string, CapabilityState>;
  overrides: ClerkOverride[];
}

export async function fetchClerkAccess(clerkId: string): Promise<ClerkAccess> {
  const res = await api.get<{ data: ClerkAccess }>(`/me/clerk-access/clerks/${clerkId}`);
  return res.data.data;
}

// ─── Barrister's clerk roster (the Clerk Permissions page) ─────

export interface ManagedClerk {
  id: string;
  role: string;
  clerk: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    memberType: string;
    avatarUrl?: string | null;
  };
  /** Current global state per grantable capability (matters/documents/calendar/emails/fees). */
  capabilities: Record<string, CapabilityState>;
  grantableCapabilities: string[];
  overrideCount: number;
  /** false = barrister has never configured this clerk (pending). */
  configured: boolean;
  assignedAt: string;
}

/** Barrister: list assigned clerks with their current capabilities. */
export async function fetchManagedClerks(): Promise<ManagedClerk[]> {
  const res = await api.get<{ data: ManagedClerk[] }>('/me/clerk-access/clerks');
  return res.data.data;
}

export interface PermissionHistoryEntry {
  id: string;
  action: string;
  capability: string | null;
  matterId: string | null;
  before: unknown;
  after: unknown;
  createdAt: string;
}

/** Barrister: access-change history for one managed clerk (newest first). */
export async function fetchClerkPermissionHistory(clerkId: string): Promise<PermissionHistoryEntry[]> {
  const res = await api.get<{ data: PermissionHistoryEntry[] }>(`/me/clerk-access/clerks/${clerkId}/log`);
  return res.data.data;
}

export async function saveClerkCapabilities(
  clerkId: string,
  capabilities: Record<string, CapabilityState>,
): Promise<void> {
  await api.put(`/me/clerk-access/clerks/${clerkId}/capabilities`, { capabilities });
}

export async function setClerkMatterOverride(
  clerkId: string,
  matterId: string,
  capability: string,
  effect: 'ALLOW' | 'DENY',
): Promise<void> {
  await api.put(`/me/clerk-access/clerks/${clerkId}/matters/${matterId}/override`, { capability, effect });
}

export async function clearClerkMatterOverride(
  clerkId: string,
  matterId: string,
  capability: string,
): Promise<void> {
  await api.delete(`/me/clerk-access/clerks/${clerkId}/matters/${matterId}/override`, { data: { capability } });
}

export async function setMatterVisibility(
  matterId: string,
  visibility: 'personal' | 'team' | 'chambers' | 'restricted',
): Promise<void> {
  await api.put(`/me/clerk-access/matters/${matterId}/visibility`, { visibility });
}

export interface MatterClerkException {
  clerkId: string;
  clerkName: string;
  capability: string;
  effect: 'ALLOW' | 'DENY';
}

export interface MatterAccess {
  visibility: 'personal' | 'team' | 'chambers' | 'restricted';
  overrides: MatterClerkException[];
}

export async function fetchMatterAccess(matterId: string): Promise<MatterAccess> {
  const res = await api.get<{ data: MatterAccess }>(`/me/clerk-access/matters/${matterId}`);
  return res.data.data;
}
